import { useQuery } from '@tanstack/react-query';
import dayjs, { type Dayjs } from 'dayjs';

import {
  AUS_METHOD_BY_NUMBER,
  LOAN_PRODUCT_TYPE_BY_NUMBER,
  LOAN_PURPOSE_TYPE_BY_NUMBER,
  OCCUPANCY_TYPE_BY_NUMBER,
  PROPERTY_TYPES_BY_NUMBER,
  REFI_PURPOSE,
} from '~/common/constants';
import useAuthentication from '~/common/hooks/useAuthentication';
import useUrl from '~/common/hooks/useUrl';
import { typedApiClient } from '~/common/util/api-client';

import type { Prettify } from '../util/types/objects';

type Values<T> = Prettify<T[keyof T]>;

export type LoanSummary = NonNullable<
  ReturnType<typeof useGetLoanSummary>['data']
>;
export type ProductType = Values<typeof LOAN_PRODUCT_TYPE_BY_NUMBER>;
export type UnderwritingMethod = Values<typeof AUS_METHOD_BY_NUMBER>;
export type PurposeType = Values<typeof LOAN_PURPOSE_TYPE_BY_NUMBER>;
type RefiPurpose = Values<typeof REFI_PURPOSE>;

function lookup<T extends Record<number, Values<T>>>(
  dictionary: T,
  value?: number,
): Values<T> | null {
  return Number.isInteger(value) ? dictionary[value] : null;
}

function select(
  data: Awaited<
    ReturnType<typeof typedApiClient.v2.loanGetLoanSummary>
  >['data'],
) {
  const { loanInfo, propertyData, loanSummaryDates } = data;
  return {
    ...data,
    loanInfo: {
      ...data.loanInfo,
      loanProductType: lookup(
        LOAN_PRODUCT_TYPE_BY_NUMBER,
        loanInfo.loanProductType,
      ),
      underwritingMethod: lookup(
        AUS_METHOD_BY_NUMBER,
        loanInfo.underwritingMethod,
      ),
      lastAusRunMethod: lookup(AUS_METHOD_BY_NUMBER, loanInfo.lastAusRunMethod),
    },
    propertyData: {
      ...data.propertyData,
      purposeType: lookup(
        LOAN_PURPOSE_TYPE_BY_NUMBER,
        propertyData.purposeType,
      ),
      occupancyType: lookup(
        OCCUPANCY_TYPE_BY_NUMBER,
        propertyData.occupancyType,
      ),
      propertyType: lookup(PROPERTY_TYPES_BY_NUMBER, propertyData.propertyType),
      refiPurpose: (propertyData.refiPurpose as RefiPurpose) || null,
    },
    loanSummaryDates: Object.entries(loanSummaryDates).reduce(
      (acc, [key, value]) => ({
        ...acc,
        [key]: value ? dayjs(value) : null,
      }),
      {} as Record<keyof typeof loanSummaryDates, Dayjs | null>,
    ),
  };
}

export const key = ['loanSummary'];

export default function useGetLoanSummary() {
  const { obfuscatedLoanIdentifier } = useUrl();
  const { obfuscatedLenderDatabaseId, obfuscatedLoanRecordId } =
    obfuscatedLoanIdentifier || {};
  const { isAuthorized } = useAuthentication();

  return useQuery({
    queryKey: key,
    async queryFn() {
      const { data } = await typedApiClient.v2.loanGetLoanSummary({
        obfuscatedLenderDatabaseId,
        obfuscatedLoanRecordId,
      });
      return select(data);
    },
    placeholderData: {
      loanInfo: {},
      propertyData: {},
      borrower: {},
      loanSummaryDates: {},
      miscellaneousInfo: {},
    } as ReturnType<typeof select>,
    enabled: !!obfuscatedLoanIdentifier && isAuthorized,
    staleTime: 60 * 60 * 1000,
  });
}
